import axios, { AxiosError, AxiosInstance } from 'axios';
import { config } from '../config';
import authService from './auth';

export interface PlayGrantResponse {
  playUrl: string;
  expiresAt?: string;
  contentType?: string;
  takeId?: string;
  role?: string;
}

export interface PlayGrantError {
  code: string;
  message: string;
  /** HTTP status from the play-grant response when available. */
  status?: number;
  /** Optional server/details payload for debugging. */
  details?: unknown;
}

function isAbsoluteHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function isPlayGrantError(error: unknown): error is PlayGrantError {
  if (!error || typeof error !== 'object') {
    return false;
  }
  // AxiosError also has string `code` + `message`; never treat it as our typed error.
  if (axios.isAxiosError(error)) {
    return false;
  }
  const candidate = error as PlayGrantError;
  return typeof candidate.code === 'string' && typeof candidate.message === 'string';
}

function readErrorPayload(data: unknown): {
  code?: string;
  message?: string;
  details?: unknown;
} | null {
  if (!data || typeof data !== 'object') {
    if (typeof data === 'string' && data.trim()) {
      return { message: data.trim() };
    }
    return null;
  }

  const root = data as Record<string, unknown>;
  const nested =
    root.error && typeof root.error === 'object'
      ? (root.error as Record<string, unknown>)
      : null;

  const codeRaw = nested?.code ?? root.code;
  const messageRaw = nested?.message ?? root.message;
  const details = nested?.details ?? root.details;

  const code = typeof codeRaw === 'string' && codeRaw.trim() ? codeRaw.trim() : undefined;
  const message =
    typeof messageRaw === 'string' && messageRaw.trim() ? messageRaw.trim() : undefined;

  if (!code && !message && details === undefined) {
    return null;
  }

  return {
    ...(code ? { code } : {}),
    ...(message ? { message } : {}),
    ...(details !== undefined ? { details } : {}),
  };
}

class AudioPlayGrantService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.axiosInstance.interceptors.request.use(async (requestConfig) => {
      const hasValidToken = await authService.ensureValidToken();
      if (hasValidToken) {
        const token = authService.getCurrentToken();
        if (token) {
          requestConfig.headers.Authorization = `Bearer ${token}`;
        }
      }
      return requestConfig;
    });
  }

  /**
   * POST playGrantUrl and return a short-lived playUrl for native `<audio>`.
   * Accepts root-relative paths (resolved against apiBaseUrl) or absolute http(s) URLs.
   */
  async requestPlayGrant(playGrantUrl: string): Promise<PlayGrantResponse> {
    const trimmed = String(playGrantUrl || '').trim();
    if (!trimmed) {
      throw {
        code: 'VALIDATION_ERROR',
        message: 'playGrantUrl is required.',
      } satisfies PlayGrantError;
    }

    try {
      const response = isAbsoluteHttpUrl(trimmed)
        ? await this.axiosInstance.post<PlayGrantResponse>(trimmed)
        : await this.axiosInstance.post<PlayGrantResponse>(trimmed);

      const body = response.data;
      if (!body || typeof body.playUrl !== 'string' || !body.playUrl.trim()) {
        throw {
          code: 'INTERNAL_ERROR',
          message: 'Play grant response was missing playUrl.',
        } satisfies PlayGrantError;
      }

      return {
        playUrl: body.playUrl.trim(),
        ...(typeof body.expiresAt === 'string' ? { expiresAt: body.expiresAt } : {}),
        ...(typeof body.contentType === 'string' ? { contentType: body.contentType } : {}),
        ...(typeof body.takeId === 'string' ? { takeId: body.takeId } : {}),
        ...(typeof body.role === 'string' ? { role: body.role } : {}),
      };
    } catch (error) {
      throw this.toPlayGrantError(error);
    }
  }

  private toPlayGrantError(error: unknown): PlayGrantError {
    if (isPlayGrantError(error)) {
      return error;
    }

    if (axios.isAxiosError(error)) {
      return this.fromAxiosError(error);
    }

    if (error instanceof Error && error.message.trim()) {
      return {
        code: 'UNKNOWN',
        message: error.message.trim(),
      };
    }

    return {
      code: 'UNKNOWN',
      message: 'Failed to get play link.',
    };
  }

  private fromAxiosError(
    axiosError: AxiosError<{ error?: { code?: string; message?: string; details?: unknown } }>
  ): PlayGrantError {
    const status = axiosError.response?.status;
    const payload = readErrorPayload(axiosError.response?.data);

    if (payload?.code || payload?.message) {
      return {
        code: String(payload.code || status || 'UNKNOWN'),
        message: String(payload.message || 'Failed to get play link.'),
        ...(typeof status === 'number' ? { status } : {}),
        ...(payload.details !== undefined ? { details: payload.details } : {}),
      };
    }

    if (!axiosError.response) {
      return {
        code: 'NETWORK_ERROR',
        message: axiosError.message || 'Network error while requesting play link.',
      };
    }

    return {
      code: String(status || 'UNKNOWN'),
      message: axiosError.message || 'Failed to get play link.',
      ...(typeof status === 'number' ? { status } : {}),
    };
  }
}

export const audioPlayGrantService = new AudioPlayGrantService();
export default audioPlayGrantService;
